import React, { useEffect, useState } from "react";
import ItemView from "./ItemView";
import GroupView from "./GroupView";
import {
  BagCheck,
  Boxes,
  Cash,
  ClockHistory,
  Funnel,
} from "react-bootstrap-icons";
import { Badge, Button, Container, Spinner } from "react-bootstrap";
import { useFormContext } from "contexts/FormContext";
import { useAppContext } from "contexts/AppContext";
import Cart from "./cart/Cart";
import Checkout from "./Checkout";
import Settings from "./Settings";
import ScrollToTop from "components/ScrollToTop";
import CustomerModal from "./CustomerModal";
import CustomerOrderHistory from "./CustomerOrderHistory";
import DefaultCurrency from "components/DefaultCurrency";
import FiltersModal from "./FiltersModal";
import { gql, useApolloClient } from "@apollo/client";

const SalesOrderForm = () => {
  // local storage
  const initialFilters = localStorage.getItem("catalog-filters");
  const hasClearedFiltersOnce = localStorage.getItem("filters-cleared");

  // contexts
  const { appState, setAppState } = useAppContext();
  const { formState, setFormState } = useFormContext();

  // states
  const [cartModal, setCartModal] = useState(false);
  const [checkoutModal, setCheckoutModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [levelsLoading, setLevelsLoading] = useState(false);
  const [cancelLevels, setCancelLevels] = useState(null);

  // itemNumber filter was not in the initial filters therefore it was not added to the local storage. hence it was added later to the initial filters.
  const [filters, setFilters] = useState(
    hasClearedFiltersOnce && initialFilters
      ? {
          ...JSON.parse(initialFilters),
          sortBy: {
            ...JSON.parse(initialFilters).sortBy,
            itemNumber: JSON.parse(initialFilters).sortBy.itemNumber
              ? JSON.parse(initialFilters).sortBy.itemNumber
              : { direction: null, position: 0 },
          },
        }
      : {
          search: "",
          itemGroups: [],
          excludedItemGroups: [],
          tags: [],
          excludedTags: [],
          onlyAvailables: false,
          withPhoto: false,
          priceFrom: null,
          priceTo: null,
          sortBy: {
            displayName: {
              direction: null,
              position: 0,
            },
            itemNumber: {
              direction: null,
              position: 0,
            },
            groupName: {
              direction: null,
              position: 0,
            },
            itemPrice: {
              direction: null,
              position: 0,
            },
            itemCreatedAt: {
              direction: null,
              position: 0,
            },
          },
          levels: null,
          activeFiltersCount: 0,
        }
  );
  const [levels, setLevels] = useState(null);

  // hooks
  const apollo = useApolloClient();

  // functions
  const openCustomerHistory = () => {
    if (!formState.info.customer) {
      setFormState({ key: "customerModal", value: true });
      return;
    }
    setFormState({ key: "customerOrderHistory", value: true });
  };

  const applyFilters = (newFilters, levels) => {
    if (newFilters.onlyAvailables && !levels) {
      setLevelsLoading(true);

      const controller = new AbortController();
      controller.signal.addEventListener("abort", () => {
        setLevelsLoading(false);
        setCancelLevels(null);
        setFilters({
          ...newFilters,
          onlyAvailables: false,
        });
      });

      setCancelLevels(controller);
      apollo
        .query({
          query: gql("{inventoryLevels(pagination: false){item available}}"),
        })
        .then((data) => {
          const levels = data.data.inventoryLevels.reduce(
            (pre, cur) => ({ ...pre, [cur.item]: cur.available }),
            {}
          );
          setLevels(levels);
          if (!controller.signal.aborted) setFilters(newFilters);
        })
        .catch((error) => {})
        .finally(() => {
          setLevelsLoading(false);
          setCancelLevels(null);
        });
    } else {
      setFilters(newFilters);
    }
  };

  // effects
  useEffect(() => {
    setFormState({
      key: "settings",
      value: {
        viewMode: localStorage.getItem("viewMode"),
      },
    });
    setAppState({
      key: "settings.active",
      value: true,
    });

    return () => {
      setAppState({
        key: "settings.active",
        value: false,
      });
      setAppState({
        key: "settings.show",
        value: false,
      });
    };
  }, []);

  useEffect(() => {
    applyFilters(filters);
  }, []);

  useEffect(() => {
    localStorage.setItem("catalog-filters", JSON.stringify(filters));
    localStorage.setItem("filters-cleared", true);
  }, [filters]);

  // storage handling
  if (localStorage.getItem("viewMode") === null)
    localStorage.setItem("viewMode", "item");

  return (
    <React.Fragment>
      <Settings />
      <CustomerModal />
      {formState.info.customer && <CustomerOrderHistory key={formState.info?.customer?.value?.id}/>}
      {/* Core Components */}
      {formState.settings?.viewMode === "item" && (
        <ItemView
          baseFilters={filters}
          setBaseFilters={setFilters}
          levels={levels}
        />
      )}
      {formState.settings?.viewMode === "group" && (
        <GroupView
          baseFilters={filters}
          setBaseFilters={setFilters}
          levels={levels}
        />
      )}
      <Cart
        show={cartModal}
        onHide={() => setCartModal(false)}
        checkout={() => setCheckoutModal(true)}
      />
      <Checkout show={checkoutModal} onHide={() => setCheckoutModal(false)} />
      <Button
        variant="success"
        className="position-fixed rounded-circle d-flex align-items-center justify-content-center"
        onClick={() => setCartModal(true)}
        style={{
          bottom: "20px",
          right: "20px",
          width: "50px",
          height: "50px",
          zIndex: 999,
        }}
      >
        <BagCheck size={25} className="position-absolute" color="white"/>
      </Button>
      <Badge
        bg="success"
        className="position-fixed p-0 d-flex align-items-center cursor-pointer"
        onClick={() => setCartModal(true)}
        style={{
          bottom: "20px",
          right: "45px",
          width: "130px",
          height: "35px",
          borderRadius: "15px 0px 0px 15px",
          zIndex: 998,
        }}
      >
        <Container className="text-start">
          <span>
            <Boxes size={16} color="white"/>{" "}
            {
              formState.details.reduce((pre, cur) => {
                if (cur.item?.id && !pre.includes(cur.item.id))
                  pre.push(cur.item.id);
                return pre;
              }, []).length
            }
          </span>
          <br />
          <Cash size={16} color="white"/>{" "}
          <DefaultCurrency
            value={formState.details.reduce(
              (pre, cur) => pre + (cur.qty || 0) * (cur.price || 0),
              0
            )}
          />
        </Container>
      </Badge>
      <Button
        variant="warning"
        className="position-fixed rounded-circle"
        onClick={openCustomerHistory}
        style={{
          bottom: "80px",
          right: "20px",
          width: "50px",
          height: "50px",
        }}
      >
        <ClockHistory size={25} />
      </Button>
      <Button
        variant="dark"
        className="position-fixed rounded-circle"
        onClick={() => setShowFilters(true)}
        style={{
          bottom: "140px",
          right: "20px",
          width: "50px",
          height: "50px",
        }}
      >
        <Funnel size={25} color="white"/>
        {filters.activeFiltersCount > 0 && (
          <Badge bg="danger" className="rounded-circle position-absolute">
            {filters.activeFiltersCount}
          </Badge>
        )}
      </Button>
      <ScrollToTop />
      <FiltersModal
        show={showFilters}
        setShow={setShowFilters}
        filters={filters}
        callback={(newFilters) => applyFilters(newFilters, levels)}
      />
      {levelsLoading && (
        <Container
          fluid
          className="p-0 m-0 d-flex justify-content-center align-items-center position-fixed h-100vh w-100vw top-0 left-0 bg-dark bg-opacity-25"
          style={{ zIndex: 10 }}
        >
          <div className="text-center bg-light rounded p-5 shadow">
            <Spinner animation="grow" />
            <h6 className="my-4">Loading inventory Levels...</h6>
            <Button
              variant="outline-dark"
              size="sm"
              onClick={() => cancelLevels.abort()}
            >
              Cancel
            </Button>
            <p className="form-text">
              If you cancel, loading levels will continue in the background
              <br />
              for future use but changes on filters will not take effect.
            </p>
          </div>
        </Container>
      )}
    </React.Fragment>
  );
};

export default SalesOrderForm;
